import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ReferenceLine,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type {
  DreTotals,
  DreComparison,
  DreMonthlyBreakdown,
  DreSection,
} from "@/hooks/use-dre";

interface DreDashboardProps {
  totals: DreTotals;
  comparison: DreComparison;
  monthlyBreakdown: DreMonthlyBreakdown[];
  sections: DreSection[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const sectionColors: Record<string, string> = {
  custo_operacional: "#ef4444",
  despesa_administrativa: "#f59e0b",
  despesa_comercial: "#8b5cf6",
  despesa_pessoal: "#3b82f6",
  despesa_financeira: "#ec4899",
  outras_despesas: "#94a3b8",
};

function ComparisonBadge({ percent }: { percent: number }) {
  if (percent === 0) {
    return (
      <div className="flex items-center gap-1">
        <Minus className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">0.0% vs anterior</span>
      </div>
    );
  }

  const isPositive = percent > 0;
  return (
    <div className="flex items-center gap-1">
      {isPositive ? (
        <TrendingUp className="h-3 w-3 text-emerald-600" />
      ) : (
        <TrendingDown className="h-3 w-3 text-red-600" />
      )}
      <span className="text-xs text-muted-foreground">
        {isPositive ? "+" : ""}
        {percent.toFixed(1)}% vs anterior
      </span>
    </div>
  );
}

export function DreDashboard({
  totals,
  comparison,
  monthlyBreakdown,
  sections,
}: DreDashboardProps) {
  // Margem liquida
  const margemLiquida =
    totals.receita_bruta > 0
      ? (totals.resultado_liquido / totals.receita_bruta) * 100
      : 0;

  // Pie chart data: expense sections only
  const expenseSections = sections.filter((s) => s.type === "despesa");
  const pieData = expenseSections.map((s) => ({
    name: s.label,
    value: Math.abs(s.total),
    section: s.section,
  }));

  // Top 5 revenues and expenses
  const allRevenueCategories = sections
    .filter((s) => s.type === "receita")
    .flatMap((s) =>
      s.categories.map((c) => ({
        ...c,
        sectionLabel: s.label,
      }))
    )
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  const allExpenseCategories = sections
    .filter((s) => s.type === "despesa")
    .flatMap((s) =>
      s.categories.map((c) => ({
        ...c,
        sectionLabel: s.label,
      }))
    )
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Chart configs
  const barChartConfig = {
    receitas: { label: "Receitas", color: "#22c55e" },
    despesas: { label: "Despesas", color: "#ef4444" },
  };

  const lineChartConfig = {
    resultado: { label: "Resultado", color: "#3b82f6" },
  };

  const pieChartConfig = Object.fromEntries(
    expenseSections.map((s) => [
      s.section,
      {
        label: s.label,
        color: sectionColors[s.section] || "#94a3b8",
      },
    ])
  );

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Receita Bruta</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(totals.receita_bruta)}
            </div>
            <ComparisonBadge percent={comparison.receita_change_percent} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Resultado Operacional</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                totals.resultado_operacional >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {formatCurrency(totals.resultado_operacional)}
            </div>
            <ComparisonBadge percent={comparison.resultado_change_percent} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Resultado Liquido</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                totals.resultado_liquido >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {formatCurrency(totals.resultado_liquido)}
            </div>
            <ComparisonBadge percent={comparison.resultado_change_percent} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Margem Liquida</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                margemLiquida >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {margemLiquida.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Resultado / Receita Bruta
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Stacked Bar Chart - Receitas vs Despesas */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Receitas vs Despesas</CardTitle>
            <CardDescription>Comparativo mensal</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyBreakdown.length > 0 ? (
              <ChartContainer
                config={barChartConfig}
                className="h-[280px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={monthlyBreakdown}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month_label"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(val) =>
                        `${(val / 1000).toFixed(0)}k`
                      }
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value) =>
                            formatCurrency(Number(value))
                          }
                        />
                      }
                    />
                    <Bar
                      dataKey="receitas"
                      name="Receitas"
                      fill="#22c55e"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="despesas"
                      name="Despesas"
                      fill="#ef4444"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponivel
              </div>
            )}
          </CardContent>
        </Card>

        {/* Line Chart - Resultado Liquido evolution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Evolucao do Resultado</CardTitle>
            <CardDescription>Resultado liquido mensal</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyBreakdown.length > 0 ? (
              <ChartContainer
                config={lineChartConfig}
                className="h-[280px] w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={monthlyBreakdown}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="month_label"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(val) =>
                        `${(val / 1000).toFixed(0)}k`
                      }
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value) =>
                            formatCurrency(Number(value))
                          }
                        />
                      }
                    />
                    <ReferenceLine
                      y={0}
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="3 3"
                    />
                    <Line
                      type="monotone"
                      dataKey="resultado"
                      name="Resultado"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#3b82f6" }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponivel
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Pie Chart - Expense composition */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Composicao de Despesas</CardTitle>
            <CardDescription>Distribuicao por secao DRE</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <>
                <ChartContainer
                  config={pieChartConfig}
                  className="h-[250px] w-full"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              sectionColors[entry.section] || "#94a3b8"
                            }
                            strokeWidth={0}
                          />
                        ))}
                      </Pie>
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) =>
                              formatCurrency(Number(value))
                            }
                          />
                        }
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </ChartContainer>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 mt-2 justify-center">
                  {pieData.map((entry) => (
                    <div
                      key={entry.section}
                      className="flex items-center gap-1.5 text-xs"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{
                          backgroundColor:
                            sectionColors[entry.section] || "#94a3b8",
                        }}
                      />
                      <span className="text-muted-foreground">
                        {entry.name}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponivel
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 5 Revenues */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Top 5 Receitas</CardTitle>
            <CardDescription>Maiores fontes de receita</CardDescription>
          </CardHeader>
          <CardContent>
            {allRevenueCategories.length > 0 ? (
              <div className="space-y-3">
                {allRevenueCategories.map((cat, index) => (
                  <div
                    key={`rev-${cat.category_id}-${index}`}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: cat.category_color }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {cat.category_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {cat.sectionLabel}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">
                      {formatCurrency(cat.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                Nenhuma receita encontrada
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 5 Expenses */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Top 5 Despesas</CardTitle>
            <CardDescription>Maiores gastos por categoria</CardDescription>
          </CardHeader>
          <CardContent>
            {allExpenseCategories.length > 0 ? (
              <div className="space-y-3">
                {allExpenseCategories.map((cat, index) => (
                  <div
                    key={`exp-${cat.category_id}-${index}`}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: cat.category_color }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {cat.category_name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {cat.sectionLabel}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-red-600 dark:text-red-400 ml-2 shrink-0">
                      {formatCurrency(cat.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                Nenhuma despesa encontrada
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
